import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { getWorkOrder, getClient, getSites, getEquipment } from "@/lib/api";
import WorkOrderTab from "@/components/WorkOrderTab";

// Full-page work order (UI sweep batch 5) — /work-orders/:id. Loads the WO plus the customer context
// WorkOrderTab needs (client, sites, equipment) and renders the tabbed job screen under the page header.
export default function WorkOrderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = location.state?.returnTo || null;
  const initialWoTab = location.state?.openWoTab || null;
  const [ctx, setCtx] = useState({ wo: null, client: null, sites: [], equipment: [], loading: true, missing: false });

  useEffect(() => {
    let alive = true;
    setCtx({ wo: null, client: null, sites: [], equipment: [], loading: true, missing: false });
    (async () => {
      try {
        const woRes = await getWorkOrder(id);
        const wo = woRes.data;
        if (!wo) throw new Error("missing");
        let client = { id: wo.client_id, name: wo.client_name };
        let sites = [];
        let equipment = [];
        if (wo.client_id) {
          const [cRes, sRes, eRes] = await Promise.allSettled([
            getClient(wo.client_id), getSites({ client_id: wo.client_id }), getEquipment({ client_id: wo.client_id }),
          ]);
          if (cRes.status === "fulfilled" && cRes.value.data) client = cRes.value.data;
          if (sRes.status === "fulfilled") sites = sRes.value.data || [];
          if (eRes.status === "fulfilled") equipment = eRes.value.data || [];
        }
        if (alive) setCtx({ wo, client, sites, equipment, loading: false, missing: false });
      } catch {
        if (alive) { setCtx((c) => ({ ...c, loading: false, missing: true })); toast.error("Work order not found"); }
      }
    })();
    return () => { alive = false; };
  }, [id]);

  const close = useCallback(() => navigate(returnTo || "/work-orders"), [navigate, returnTo]);
  const getSiteNameById = useCallback((siteId) => (ctx.sites || []).find((s) => s.id === siteId)?.name || "", [ctx.sites]);

  if (ctx.loading) {
    return (
      <div className="flex justify-center py-16" data-testid="wo-page-loading">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand" />
      </div>
    );
  }
  if (ctx.missing || !ctx.wo) {
    return (
      <div className="py-16 text-center text-sm text-slate-500" data-testid="wo-page-missing">
        This work order could not be found.{" "}
        <button type="button" className="text-brand hover:underline" onClick={close}>Back to Work Orders</button>
      </div>
    );
  }

  const { wo, client } = ctx;
  return (
    <div data-testid="wo-detail-page">
      <WorkOrderTab
        fullPage
        onClose={close}
        clientId={client.id}
        clientName={client.name}
        clientSites={ctx.sites}
        clientEquipment={ctx.equipment}
        getSiteNameById={getSiteNameById}
        selectedSiteId={wo.site_id || ""}
        openWorkOrderId={wo.id}
        onWorkOrderOpened={() => {}}
        returnTo={returnTo}
        initialWoTab={initialWoTab}
        onReturnHandled={() => {}}
        clientHideTax={!!client.hide_tax}
        clientBillingContact={{
          name: client.billing_contact_name || "",
          email: client.billing_contact_email || "",
          phone: client.billing_contact_phone || "",
        }}
        clientCustomerNumber={client.customer_number || ""}
        clientCustomerType={client.customer_type || ""}
      />
    </div>
  );
}
